import { apiClient } from './apiClient';
import { SyncType } from '../types/sync/enums';
import type { SyncStatusResponse } from '../types/sync/syncStatusResponse';
import type { AcombaCustomer, AcombaInvoiceAr, AcombaCustomerPayment, AcombaPaymentLink } from '../types/acombaBridge';

type RawSyncStatusResponse = Omit<SyncStatusResponse, 'startedAt' | 'deadline' | 'lastSyncedAt' | 'lastFullSyncAt'> & {
    startedAt: string | null;
    deadline: string | null;
    lastSyncedAt: string | null;
    lastFullSyncAt?: string | null;
};

function parseResponse(raw: RawSyncStatusResponse): SyncStatusResponse {
    return {
        ...raw,
        startedAt: raw.startedAt ? new Date(raw.startedAt) : null,
        deadline: raw.deadline ? new Date(raw.deadline) : null,
        lastSyncedAt: raw.lastSyncedAt ? new Date(raw.lastSyncedAt) : null,
        lastFullSyncAt: raw.lastFullSyncAt ? new Date(raw.lastFullSyncAt) : null,
    };
}

async function* mapStream(source: AsyncGenerator<RawSyncStatusResponse>): AsyncGenerator<SyncStatusResponse> {
    for await (const item of source) {
        yield parseResponse(item);
    }
}

export interface BridgeSyncRequest {
    syncType: SyncType;
    companyIdentity: string;
    highWaterMark: number | null;
    acombaVersion: string | null;
    fetchedAt: string;
    customers: AcombaCustomer[];
    invoices: AcombaInvoiceAr[];
    payments: AcombaCustomerPayment[];
    // The bridge's live FunctionalityAccess.relance flag at extraction time. The backend persists it
    // (see docs/features/auth/section-access.md) so Home/History can gate section visibility without
    // depending on the bridge being reachable. Null when the bridge never reported it — the backend
    // then keeps its last persisted decision rather than overwriting it with an unknown.
    relanceAccess: boolean | null;
    transphereActive: boolean | null;
    // The two halves of "this société can be paid through Transphere", combined backend-side.
    transphereEncaissementActif: boolean | null;
    transphereReceiptSubscription: boolean | null;
    /** Whether the extraction read every customer. False ⇒ the backend must not treat an absence as "nothing owed". */
    isCompleteRead: boolean;
    // ACCEO Transphere payment links the bridge resolved on the Acomba machine. Null means the extraction
    // could not resolve them (no credential, Transphere unreachable, older bridge); [] means it did and
    // found none. The backend replaces a relance's stored set from this, so null must leave the stored
    // links alone while [] legitimately clears them.
    paymentLinks: AcombaPaymentLink[] | null;
}

const syncService = {
    // The AcceoConnect token is never sent in the body — the backend sources it from the server-side
    // Connect session (portal) or the X-Connect-Token header (desktop).
    triggerConnect: (noAi = false, syncType = SyncType.Full, regenerate = false) =>
        apiClient.post<void>('/sync/trigger/connect', { noAi, syncType, regenerate }),

    triggerBridge: (payload: BridgeSyncRequest, regenerate = false) =>
        apiClient.post<void>(regenerate ? '/sync/trigger/bridge?regenerate=true' : '/sync/trigger/bridge', payload),

    getStatus: () =>
        apiClient.get<RawSyncStatusResponse>('/sync/status').then(parseResponse),

    // Streams every progress snapshot until the server closes the stream
    // (Completed or Failed terminal snapshot) or the consumer aborts.
    streamProgress: (signal?: AbortSignal) =>
        mapStream(apiClient.sseStream<RawSyncStatusResponse>('/sync/events', signal)),

    // startDate: the configured value (null ⇒ none set). effectiveStartDate: the value the backend
    // actually filters by — the configured date, or the 6-month rolling default when none is set.
    getSyncStartDate: () =>
        apiClient.get<{ startDate: string | null; effectiveStartDate: string }>('/sync/start-date'),

    setSyncStartDate: (date: string) =>
        apiClient.put<void>('/sync/start-date', { startDate: date }),

    // Reports the bridge's live FunctionalityAccess at session start so the backend's persisted
    // decisions are fresh from the moment the app opens. isAdministrator is undefined on a bridge that
    // does not report it, which the backend leaves alone rather than overwriting.
    setRelanceAccess: (hasAccess: boolean, isAdministrator?: boolean) =>
        apiClient.post<void>('/session/relance-access', { hasAccess, isAdministrator }),

    // Reports the société's own name and email, read from Acomba's CompanyInformation card at session
    // start. They become the display name and reply-to on reminders sent from a desktop session, which
    // has no AcceoConnect sender-identity endpoint to ask instead.
    setCompanyIdentity: (name: string | null, email: string | null) =>
        apiClient.post<void>('/session/company-identity', { name, email }),
};

export { syncService };
